// Core packages
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Third party packages
import { MatDialog, Sort } from '@angular/material';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { InterventionsService } from 'src/app/modules/interventions/interventions.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { Intervention } from 'src/app/shared/models/intervention/intervention.model';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { DateTimeFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { AddContractorDialogComponent } from '../../components/add-contractor-dialog/add-contractor-dialog.component';
import { AddReceivedDocumentDialogComponent } from '../../components/add-receivedDocument-dialog/add-receivedDocument-dialog.component';
import { AddTaxDeductionFeeDialogComponent } from '../../components/add-taxDeductionFee-dialog/add-taxDeductionFee-dialog.component';
import { EditContractorDialogComponent } from '../../components/edit-contractor-dialog/edit-contractor-dialog.component';
import { EditReceivedDocumentDialogComponent } from '../../components/edit-receivedDocument-dialog/edit-receivedDocument-dialog.component';
import { EditTaxDeductionFeeDialogComponent } from '../../components/edit-taxDeductionFee-dialog/edit-taxDeductionFee-dialog.component';

@Component({
  selector: 'app-intervention-detail',
  templateUrl: './intervention-detail.page.html',
  styleUrls: ['./intervention-detail.page.scss']
})
export class InterventionDetailComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  intervention: Intervention = new Intervention();
  sortedContractorsData = [];
  initialContractorsData = [];
  sortedTaxDeductionFeesData = [];
  initialTaxDeductionFeesData = [];
  sortedReceivedDocumentsData = [];
  initialReceivedDocumentsData = [];

  detailCardIsLoading = true;
  deleteCardIsLoading = true;
  contractorsCardIsLoading = true;
  taxDeductionFeesCardIsLoading = true;
  receivedDocumentsCardIsLoading = true;

  // BEGIN - Add intervention form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  notesField: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.save'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-save',
      inline: true,
    },
  };
  // END - Add intervention form
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.delete'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'warn',
    spinnerColor: 'warn',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-trash',
      inline: true,
    },
  };
  dateTimeFormat: DateTimeFormatPipe;

  displayedTotal = 0;
  previousValue: any = {};
  availableOptions = [];
  subscription: any;
  notes: any[];
  companyId: string;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private interventionsService: InterventionsService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('interventions.detail.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Get requested intervention data
    const interventionId = this.activatedRoute.snapshot.params.interventionId;
    this.subscriptions.push(
      this.interventionsService.get(interventionId).subscribe(
        res => {
          if (res.status) {
            this.loadInterventionData(res.data);
            return;
          }
          // Something bad happened
          // redirect user to interventions list
          this.router.navigate([this.lang, 'app', 'interventions', 'list']);
        },
        err => {
          // if we came here users with given userId do not exist
          this.helperService.handleError(err);
          this.router.navigate([this.lang, 'errors', '404']);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all interventions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }


  /**
   * Handle params change
   *
   * @since 1.0.0
   */
  loadInterventionData(intervention: any): void {
    // Set permission data
    this.intervention.setData(intervention);

    // Set contractors
    this.sortedContractorsData = this.intervention.get("contractors");
    this.initialContractorsData = JSON.parse(JSON.stringify(this.sortedContractorsData));
    this.contractorsCardIsLoading = false;

    // Set taxDeductionFees
    this.sortedTaxDeductionFeesData = this.intervention.get("taxDeductionFees");
    this.initialTaxDeductionFeesData = JSON.parse(JSON.stringify(this.sortedTaxDeductionFeesData));
    this.taxDeductionFeesCardIsLoading = false;

    // Set receivedDocuments
    this.sortedReceivedDocumentsData = this.intervention.get("receivedDocuments");
    this.initialReceivedDocumentsData = JSON.parse(JSON.stringify(this.sortedReceivedDocumentsData));
    this.receivedDocumentsCardIsLoading = false;

    // Stop loading in delete form
    this.deleteCardIsLoading = false;

    // Init edit form
    this.initForm();

    // Make detail form card visible by disabling his card-spinner
    this.detailCardIsLoading = false;
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.intervention.getData();

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12 col-sm-6',
            type: 'input',
            key: 'name',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.name'),
              placeholder: this.translateService.instant('interventions.fields.name'),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-sm-6',
            type: 'input',
            key: 'propertyName',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.propertyName'),
              placeholder: this.translateService.instant('interventions.fields.propertyName'),
              required: false,
              minLength: 2,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'input',
            key: 'type',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.type'),
              placeholder: this.translateService.instant('interventions.fields.type'),
              required: false,
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'datepicker',
            key: 'meetingResolutionDate',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.meetingResolutionDate'),
              placeholder: this.translateService.instant('interventions.fields.meetingResolutionDate'),
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'input',
            key: 'totalFee',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.totalFee'),
              placeholder: this.translateService.instant('interventions.fields.totalFee'),
              required: false,
              type: 'number',
              step: 0.1,
              min: 0,
              // max: 10000,
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'input',
            key: 'constructionManagement',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.constructionManagement'),
              placeholder: this.translateService.instant('interventions.fields.constructionManagement'),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'input',
            key: 'securityCoordinator',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.securityCoordinator'),
              placeholder: this.translateService.instant('interventions.fields.securityCoordinator'),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'datepicker',
            key: 'constructionStart',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.constructionStart'),
              placeholder: this.translateService.instant('interventions.fields.constructionStart'),
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'datepicker',
            key: 'constructionEnd',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.constructionEnd'),
              placeholder: this.translateService.instant('interventions.fields.constructionEnd'),
            },
          },
          {
            className: 'col-12 col-sm-6 col-md-4 col-lg-3',
            type: 'input',
            key: 'taxDeductionType',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.taxDeductionType'),
              placeholder: this.translateService.instant('interventions.fields.taxDeductionType'),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'notes',
            templateOptions: {
              label: this.translateService.instant('interventions.fields.notes'),
              placeholder: this.translateService.instant('interventions.fields.notes'),
              required: false,
              minLength: 1,
              maxLength: 5000,
              rows: 5,
            },
            expressionProperties: {
              'templateOptions.description': '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            }
          }
        ],
      },
    ];
  }

  /**
   * Open contractor add dialog
   *
   * @since 1.0.0
   */
  onAddContractor(): void {
    if (!this.intervention || !this.intervention.getId()) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddContractorDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId()
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (typeof res !== 'undefined'
            && res.newIntervention
            && res.newIntervention.contractors
          ) {
            this.sortedContractorsData = res.newIntervention.contractors;
            this.intervention.setData(res.newIntervention);
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Open contractor add dialog
   *
   * @since 1.0.0
   */
  onEditContractor(contractor: any): void {
    if (!this.intervention || !this.intervention.getId() || !contractor || !contractor._id) {
      this.toastrService.error(this.translateService.instant('interventions.errors.missing_contractor'));
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(EditContractorDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId(),
        contractor,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed().subscribe(
      res => {
        if (typeof res !== 'undefined'
          && res.updatedIntervention
          && res.updatedIntervention.contractors
        ) {
          this.sortedContractorsData = res.updatedIntervention.contractors;
          this.intervention.setData(res.updatedIntervention);
          return;
        }
      },
      err => {
        this.helperService.handleError(err);
      }
    ));
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortContractorsData(sort: Sort): any {
    const data = this.sortedContractorsData;
    if (!sort.active || sort.direction === '') {
      this.sortedContractorsData = JSON.parse(JSON.stringify(this.initialContractorsData));
      return;
    }

    this.sortedContractorsData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'fee': return this.compare(a.fee, b.fee, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Open taxDeductionFee add dialog
   *
   * @since 1.0.0
   */
  onAddTaxDeductionFee(): void {
    if (!this.intervention || !this.intervention.getId()) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddTaxDeductionFeeDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId()
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (typeof res !== 'undefined'
            && res.newIntervention
            && res.newIntervention.taxDeductionFees
          ) {
            this.sortedTaxDeductionFeesData = res.newIntervention.taxDeductionFees;
            this.intervention.setData(res.newIntervention);
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Open taxDeductionFee add dialog
   *
   * @since 1.0.0
   */
  onEditTaxDeductionFee(taxDeductionFee: any): void {
    if (!this.intervention || !this.intervention.getId() || !taxDeductionFee || !taxDeductionFee._id) {
      this.toastrService.error(this.translateService.instant('interventions.errors.missing_taxDeductionFee'));
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(EditTaxDeductionFeeDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId(),
        taxDeductionFee,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed().subscribe(
      res => {
        if (typeof res !== 'undefined'
          && res.updatedIntervention
          && res.updatedIntervention.taxDeductionFees
        ) {
          this.sortedTaxDeductionFeesData = res.updatedIntervention.taxDeductionFees;
          this.intervention.setData(res.updatedIntervention);
          return;
        }
      },
      err => {
        this.helperService.handleError(err);
      }
    ));
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortTaxDeductionFeesData(sort: Sort): any {
    const data = this.sortedTaxDeductionFeesData;
    if (!sort.active || sort.direction === '') {
      this.sortedTaxDeductionFeesData = JSON.parse(JSON.stringify(this.initialTaxDeductionFeesData));
      return;
    }

    this.sortedTaxDeductionFeesData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'year': return this.compare(a.year, b.year, isAsc);
        case 'fee': return this.compare(a.fee, b.fee, isAsc);
        case 'resolved': return this.compare(a.resolved, b.resolved, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Toggle taxDeductionFee's "resolved" property
   * and updates the whole intervention
   *
   * @since 1.0.0
   */
  onToggleTaxDeductionFee(itemId: string, checked: boolean) {
    const taxDeductionFees = this.intervention.get("taxDeductionFees");
    if (Array.isArray(taxDeductionFees) && taxDeductionFees.length) {
      const element = taxDeductionFees.find((el: any) => el._id === itemId);
      if (element) {
        element.resolved = checked;
      }
    } else {
      this.toastrService.error("Error: taxDeductionFees is not an array");
    }

    const model = JSON.parse(JSON.stringify(this.intervention.getData()));
    this.subscriptions.push(
      this.interventionsService.update(this.intervention.getId(), model)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status && res.data) {
              this.intervention.setData(res.data);
              this.toastrService.success(res.message);
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Open receivedDocument add dialog
   *
   * @since 1.0.0
   */
  onAddReceivedDocument(): void {
    if (!this.intervention || !this.intervention.getId()) {
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(AddReceivedDocumentDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId()
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed()
      .subscribe(
        res => {
          if (typeof res !== 'undefined'
            && res.newIntervention
            && res.newIntervention.receivedDocuments
          ) {
            this.sortedReceivedDocumentsData = res.newIntervention.receivedDocuments;
            this.intervention.setData(res.newIntervention);
            return;
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Open receivedDocument add dialog
   *
   * @since 1.0.0
   */
  onEditReceivedDocument(receivedDocument: any): void {
    if (!this.intervention || !this.intervention.getId() || !receivedDocument || !receivedDocument._id) {
      this.toastrService.error(this.translateService.instant('interventions.errors.missing_receivedDocument'));
      return;
    }

    // Open dialog
    const dialogRef = this.dialog.open(EditReceivedDocumentDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: {
        interventionId: this.intervention.getId(),
        receivedDocument,
      }
    });

    // On dialog closing, check if something is changed
    // If so, let's reload table data
    this.subscriptions.push(dialogRef.afterClosed().subscribe(
      res => {
        if (typeof res !== 'undefined'
          && res.updatedIntervention
          && res.updatedIntervention.receivedDocuments
        ) {
          this.sortedReceivedDocumentsData = res.updatedIntervention.receivedDocuments;
          this.intervention.setData(res.updatedIntervention);
          return;
        }
      },
      err => {
        this.helperService.handleError(err);
      }
    ));
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortReceivedDocumentsData(sort: Sort): any {
    const data = this.sortedReceivedDocumentsData;
    if (!sort.active || sort.direction === '') {
      this.sortedReceivedDocumentsData = JSON.parse(JSON.stringify(this.initialReceivedDocumentsData));
      return;
    }

    this.sortedReceivedDocumentsData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'name': return this.compare(a.name, b.name, isAsc);
        case 'description': return this.compare(a.description, b.description, isAsc);
        case 'received': return this.compare(a.received, b.received, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Toggle receivedDocument's "received" property
   * and updates the whole intervention
   *
   * @since 1.0.0
   */
  onToggleReceivedDocument(itemId: string, checked: boolean) {
    const receivedDocuments = this.intervention.get("receivedDocuments");
    if (Array.isArray(receivedDocuments) && receivedDocuments.length) {
      const element = receivedDocuments.find((el: any) => el._id === itemId);
      if (element) {
        element.received = checked;
      }
    } else {
      this.toastrService.error("Error: receivedDocuments is not an array");
    }

    const model = JSON.parse(JSON.stringify(this.intervention.getData()));
    this.subscriptions.push(
      this.interventionsService.update(this.intervention.getId(), model)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status && res.data) {
              this.intervention.setData(res.data);
              this.toastrService.success(res.message);
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
    );
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Handle intervention status change
   *
   * @since 1.0.0
   */
  onStatusChange() {
    const status = this.model.status;
    if (!status) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('interventions.status.changeMessage'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            // User hit decline button
            return;
          }

          // User hit confirm button
          this.subscriptions.push(
            this.interventionsService.changeStatus(this.intervention.getId(), { status })
              .subscribe(
                res2 => {
                  if (res2.status && res2.intervention) {
                    const newIntervention = {
                      ...this.intervention.getData(),
                      status: res2.intervention.status
                    };
                    this.toastrService.success(res2.message);
                    this.loadInterventionData(newIntervention);
                  }
                },
                err => {
                  // Choose one of the following error handling
                  // method. The first one shows a message right
                  // under the form fields (if the form is properly
                  // setted), the second one shows toastr
                  // notifications for each error
                  // this.helperService.handleFormError(this.passwordForm, err);
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    // Handle intervention notes
    // if (this.model.notes.text) {
    //   this.notes.push({
    //     text: this.model.notes.text,
    //     createdAt: moment().toISOString(),
    //     createdBy: this.loggedUser._id,
    //   });
    //   this.model.notes = this.notes;
    // }

    this.subscriptions.push(
      this.interventionsService.update(this.intervention.getId(), this.model)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              const previousProperty = this.intervention.get("property");
              this.intervention.setData(res.data);
              this.intervention.set("property", previousProperty);

              // // Add string name to the last created note (so we can inject it in dialog)
              // const lastNote = this.notes[this.notes.length - 1];
              // if (lastNote) {
              //   const newNote = {
              //     ...lastNote,
              //     createdBy: {
              //       _id: lastNote.createdBy,
              //       name: `${this.loggedUser.firstName} ${this.loggedUser.lastName}`
              //     },
              //   };
              //   this.notes[this.notes.length - 1] = newNote;
              // }
              this.toastrService.success(res.message);
            }
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('dialogs.confirm.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.subscriptions.push(
            this.interventionsService.delete(this.intervention.getId())
              .pipe(
                finalize(() => this.deleteBtnOptions.active = false)
              )
              .subscribe(
                res2 => {
                  this.toastrService.success(res2.message);
                  // Once the user is deleted, redirect to list
                  this.router.navigate([this.lang, 'app', 'interventions', 'list']);
                  return;
                },
                err => {
                  // Choose one of the following error handling
                  // method. The first one shows a message right
                  // under the form fields (if the form is properly
                  // setted), the second one shows toastr
                  // notifications for each error
                  // this.helperService.handleFormError(this.passwordForm, err);
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }
}
